/*
 * Personalització dels carrusels de casos d'ús.
 *
 * Si el visitant ha acceptat les galetes de «Preferències» (Pragmatic Web Toolkit), es guarda al seu
 * navegador (localStorage) quins tipus de negoci, sectors, serveis i àrees ha consultat. Els carrusels
 * reordenen la bossa de casos que envia el servidor per mostrar primer els més afins.
 * Sense consentiment no es guarda res i es manté l'ordre del servidor (aleatori ponderat per prioritat).
 */

const STORAGE_KEY = 'pragmatic_casos_interessos';
const CONSENT_CATEGORY = 'preferences';
const DECAY = 0.9; // a cada pàgina vista, l'interès antic perd pes
const MAX_KEYS = 40;
const MAX_PER_PERFIL = 3; // diversitat: no més de 3 casos del mateix tipus de negoci

const WEIGHTS = { perfils: 1, sectors: 0.6, serveis: 0.5, arees: 0.4 };

const emptyStore = () => ({ v: 1, perfils: {}, sectors: {}, serveis: {}, arees: {} });

function hasConsent() {
  return !!(window.PragmaticCookies && window.PragmaticCookies.hasConsent(CONSENT_CATEGORY));
}

function read() {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return data && data.v === 1 ? data : emptyStore();
  } catch (e) {
    return emptyStore();
  }
}

function write(store) {
  if (!hasConsent()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    // sense emmagatzematge disponible: no passa res
  }
}

function prune(bucket) {
  const entries = Object.entries(bucket).filter(([, score]) => score > 0.05);
  entries.sort((a, b) => b[1] - a[1]);
  return Object.fromEntries(entries.slice(0, MAX_KEYS));
}

function add(store, signals, amount) {
  Object.entries(signals).forEach(([type, id]) => {
    if (!id || !store[type]) return;
    store[type][id] = (store[type][id] || 0) + amount;
  });
}

function decay(store) {
  Object.keys(WEIGHTS).forEach((type) => {
    Object.keys(store[type]).forEach((id) => {
      store[type][id] *= DECAY;
    });
    store[type] = prune(store[type]);
  });
}

// Pàgina actual: tipus de negoci (i sector) o servei
function recordPageView() {
  if (!hasConsent()) return;
  const context = document.querySelector('[data-interes-pagina]');
  const store = read();
  decay(store);
  if (context) {
    add(store, {
      perfils: context.dataset.perfil,
      sectors: context.dataset.sector,
      serveis: context.dataset.servei,
    }, 3);
  }
  write(store);
}

function recordSignals(signals, amount) {
  if (!hasConsent()) return;
  const store = read();
  add(store, signals, amount);
  write(store);
}

// Clics: targetes de carrusel, filtres d'àrea i casos desplegats a la landing
function bindClickSignals() {
  document.addEventListener('click', (event) => {
    const card = event.target.closest('[data-casos-card]');
    if (card) {
      recordSignals({
        perfils: card.dataset.perfil,
        sectors: card.dataset.sector,
        serveis: card.dataset.servei,
        arees: card.dataset.area,
      }, 2);
      return;
    }

    const filter = event.target.closest('[data-area-id]');
    if (filter && filter.matches('button')) {
      recordSignals({ arees: filter.dataset.areaId }, 1);
    }
  });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const article = details.closest('article[data-area-id]');
    if (article) {
      recordSignals({ arees: article.dataset.areaId, serveis: article.dataset.serveiId }, 1);
    }
  }, true);
}

function score(card, store, maxima) {
  let total = 0;
  [['perfils', 'perfil'], ['sectors', 'sector'], ['serveis', 'servei'], ['arees', 'area']].forEach(([type, attr]) => {
    const id = card.dataset[attr];
    if (id && store[type][id] && maxima[type] > 0) {
      total += WEIGHTS[type] * (store[type][id] / maxima[type]);
    }
  });
  return total;
}

// Reordena un carrusel: interès del visitant + ordre del servidor + una mica d'atzar
export function personalitzaCarrusel(root) {
  const track = root.querySelector('[data-casos-track]');
  if (!track) return;
  const cards = [...track.querySelectorAll('[data-casos-card]')];
  const visibles = parseInt(root.dataset.casosVisibles || '12', 10);

  const store = hasConsent() ? read() : emptyStore();
  const maxima = {};
  Object.keys(WEIGHTS).forEach((type) => {
    maxima[type] = Math.max(0, ...Object.values(store[type]));
  });
  const hasHistory = Object.values(maxima).some((value) => value > 0);
  if (!hasHistory) return; // sense historial: es manté la selecció del servidor

  const n = cards.length;
  const ranked = cards
    .map((card, index) => ({
      card,
      value: 2 * score(card, store, maxima) + (n - index) / n + Math.random() * 0.3,
    }))
    .sort((a, b) => b.value - a.value);

  const perPerfil = {};
  const chosen = [];
  const rest = [];
  ranked.forEach(({ card }) => {
    const perfil = card.dataset.perfil;
    if (chosen.length < visibles && (perPerfil[perfil] || 0) < MAX_PER_PERFIL) {
      perPerfil[perfil] = (perPerfil[perfil] || 0) + 1;
      chosen.push(card);
    } else {
      rest.push(card);
    }
  });
  // Si la diversitat ha deixat buits, s'omplen amb la resta
  while (chosen.length < visibles && rest.length) {
    chosen.push(rest.shift());
  }

  chosen.forEach((card) => {
    card.classList.remove('hidden');
    track.appendChild(card);
  });
  rest.forEach((card) => {
    card.classList.add('hidden');
    track.appendChild(card);
  });
  track.scrollLeft = 0;
}

export function initCasosInteressos() {
  bindClickSignals();
  recordPageView();

  // Si el visitant accepta les preferències més tard, comencem a registrar des d'aquesta pàgina
  if (window.PragmaticCookies && !hasConsent()) {
    window.PragmaticCookies.onConsent(CONSENT_CATEGORY, recordPageView);
  }
}
